// v2/components/ProductDetailModal.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Package, Minus, Plus, ShoppingCart, Zap, Warehouse, Truck, CheckCircle2, XCircle } from 'lucide-react';
import { WAREHOUSES, DELIVERY_TIME } from '../constants/catalog';
import { useMiktarTaslak } from '../utils/miktarTaslak';
import FitmentInfo, { FitmentLogos } from './FitmentInfo';
import { formatTRY, formatNumber, formatPercent } from '../utils/format';
import { withVat, VAT_RATE } from '../utils/discount';
import styles from './ProductDetailModal.module.css';

/** Basili tutunca hizlanarak tekrar eden aksiyon */
function useHoldRepeat(action) {
  const timer = useRef(null);
  const count = useRef(0);

  const stop = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    count.current = 0;
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback(() => {
    action();
    const tick = () => {
      count.current += 1;
      action();
      timer.current = setTimeout(tick, count.current > 6 ? 40 : 90);
    };
    timer.current = setTimeout(tick, 400);
  }, [action]);

  return {
    onPointerDown: (e) => { if (e.button === 0 || e.pointerType !== 'mouse') start(); },
    onPointerUp: stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
  };
}

/** Degisimde sayarak gecen deger */
function useCountUp(target, duration = 420) {
  const [value, setValue] = useState(target);
  const raf = useRef(null);
  const from = useRef(target);

  useEffect(() => {
    const reduce = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !Number.isFinite(target)) { setValue(target); from.current = target; return undefined; }

    const start = from.current;
    const delta = target - start;
    if (delta === 0) return undefined;

    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const tick = (now) => {
      const pr = Math.min((now - t0) / duration, 1);
      const v = start + delta * (1 - Math.pow(1 - pr, 3));
      setValue(v);
      if (pr < 1) raf.current = requestAnimationFrame(tick);
      else from.current = target;
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target, duration]);

  return value;
}

/** Tam ekran gorsel goruntuleyici — ok tuslari ve butonlarla gezinilir,
 *  Esc ile kapanir. Arka plana tiklamak da kapatir. */
export function TamEkran({ kareler, baslangic, onClose, baslik, code = '' }) {
  const [i, setI] = React.useState(baslangic);
  const dar = typeof window !== 'undefined' && window.innerWidth <= 700;

  const onceki = React.useCallback(
    () => setI((n) => (n - 1 + kareler.length) % kareler.length), [kareler.length]);
  const sonraki = React.useCallback(
    () => setI((n) => (n + 1) % kareler.length), [kareler.length]);

  React.useEffect(() => {
    const tus = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onceki();
      else if (e.key === 'ArrowRight') sonraki();
    };
    document.addEventListener('keydown', tus);
    // arkadaki sayfa kaymasin
    const eskiOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', tus);
      document.body.style.overflow = eskiOverflow;
    };
  }, [onClose, onceki, sonraki]);

  return (
    <div
      className={`${styles.teKatman} ${code ? styles.teKatmanFit : ''}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${baslik} — görsel ${i + 1}/${kareler.length}`}
    >
      <button type="button" className={styles.teKapat}
              onClick={onClose} aria-label="Kapat">
        <X size={22} />
      </button>

      {kareler.length > 1 && (
        <button type="button" className={`${styles.teOk} ${styles.teOkSol}`}
                onClick={(e) => { e.stopPropagation(); onceki(); }}
                aria-label="Önceki görsel">‹</button>
      )}

      <img
        className={styles.teGorsel}
        src={kareler[i]}
        alt={baslik}
        onClick={(e) => e.stopPropagation()}
        decoding="async"
      />

      {kareler.length > 1 && (
        <button type="button" className={`${styles.teOk} ${styles.teOkSag}`}
                onClick={(e) => { e.stopPropagation(); sonraki(); }}
                aria-label="Sonraki görsel">›</button>
      )}

      {kareler.length > 1 && (
        <div className={styles.teSayac}>{i + 1} / {kareler.length}</div>
      )}

      {code && (
        <div className={styles.teUyum} onClick={(e) => e.stopPropagation()}>
          <span className={styles.teUyumBaslik}>Araç Uyumu</span>
          <FitmentLogos code={code} limit={dar ? 5 : 10} size={dar ? 'sm' : 'lg'} koyu />
        </div>
      )}
    </div>
  );
}

/** Urun gorsel galerisi — yuklenemeyen kareler kendini gizler.
 *  Urunlerin %96'sinda 4 gorsel var, kalanlarda eksik kare hic gosterilmez. */
function Galeri({ product }) {
  const kareler = React.useMemo(
    () => (product.images && product.images.length
      ? product.images
      : (product.image ? [product.image] : [])),
    [product.images, product.image]
  );

  const [aktif, setAktif] = React.useState(0);
  const [bozuk, setBozuk] = React.useState({});
  const [tamEkran, setTamEkran] = React.useState(false);

  // Urun degisince secim sifirlansin
  React.useEffect(() => { setAktif(0); setBozuk({}); }, [product.productCode]);

  const gecerli = kareler.filter((_, i) => !bozuk[i]);
  const aktifSrc = kareler[aktif] && !bozuk[aktif] ? kareler[aktif] : gecerli[0];

  if (!aktifSrc) {
    return (
      <div className={styles.hero}>
        <span className={styles.heroFallback}><Package size={56} strokeWidth={1.1} /></span>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`${styles.hero} ${styles.heroBtn}`}
        onClick={() => setTamEkran(true)}
        aria-label="Görseli tam ekran aç"
        title="Büyütmek için tıklayın"
      >
        <img
          className={styles.heroImg}
          src={aktifSrc}
          alt={product.description}
          decoding="async"
        />
        <span className={styles.heroBuyut}>⤢</span>
      </button>
      {gecerli.length > 1 && (
        <div className={styles.galeriSerit}>
          {kareler.map((src, i) => (bozuk[i] ? null : (
            <button
              key={src}
              type="button"
              className={`${styles.galeriKare} ${i === aktif ? styles.galeriKareAktif : ''}`}
              onClick={() => setAktif(i)}
              aria-label={`Görsel ${i + 1}`}
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                decoding="async"
                onError={() => setBozuk((b) => ({ ...b, [i]: true }))}
              />
            </button>
          )))}
        </div>
      )}

      {tamEkran && (
        <TamEkran
          code={product.productCode}
          kareler={gecerli}
          baslangic={Math.max(gecerli.indexOf(aktifSrc), 0)}
          baslik={product.description}
          onClose={() => setTamEkran(false)}
        />
      )}
    </>
  );
}

export default function ProductDetailModal({ product, onClose, onAddToCart, eurRate = 0 }) {
  const [whKey, setWhKey] = useState(WAREHOUSES[0].key);
  const [raw, setRaw] = useState('1');
  const modalRef = useRef(null);
  const closeRef = useRef(null);

  // Listede girilen depo+miktar varsa pencere onunla acilir
  const taslak = useMiktarTaslak(product ? (product.productCode || product.code) : null);
  useEffect(() => {
    if (!product) return;
    const t = taslak && product[taslak.whKey] > 0 ? taslak : null;
    if (t) {
      setWhKey(t.whKey);
      setRaw(String(Math.min(t.qty, Math.max(product[t.whKey], 1))));
      return;
    }
    const first = WAREHOUSES.find((w) => product[w.key] > 0);
    setWhKey(first ? first.key : WAREHOUSES[0].key);
    setRaw('1');
  }, [product, taslak]);

  // ESC + Tab dongusu (focus trap)
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key !== 'Tab' || !modalRef.current) return;

    const items = modalRef.current.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }, [onClose]);
  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prev;
    };
  }, [handleKey]);

  // acilista odak modalin icine gelsin
  useEffect(() => {
    if (!product) return;
    const t = setTimeout(() => { if (closeRef.current) closeRef.current.focus(); }, 30);
    return () => clearTimeout(t);
  }, [product]);

  // --- hook'lar erken return'den ONCE calismali (rules-of-hooks) ---
  // product yokken de guvenli hesaplanir, deger kullanilmaz
  const safeWh = WAREHOUSES.find((w) => w.key === whKey) || WAREHOUSES[0];
  const safeStock = product ? product[safeWh.key] : 0;
  const safeQty = Math.min(Math.max(parseInt(raw, 10) || 1, 1), Math.max(safeStock, 1));
  const safeTotal = product ? withVat(product.netPrice * safeQty) : 0;

  const decQty = useCallback(() => setRaw((cur) => {
    const n = parseInt(cur, 10) || 1;
    return String(Math.max(n - 1, 1));
  }), []);

  const incQty = useCallback(() => setRaw((cur) => {
    const n = parseInt(cur, 10) || 1;
    return String(Math.min(n + 1, Math.max(safeStock, 1)));
  }), [safeStock]);

  const holdDec = useHoldRepeat(decQty);
  const holdInc = useHoldRepeat(incQty);
  const shownTotal = useCountUp(safeTotal);

  if (!product) return null;

  const wh = WAREHOUSES.find((w) => w.key === whKey) || WAREHOUSES[0];
  const stock = product[wh.key];
  const out = stock <= 0;
  const qty = Math.min(Math.max(parseInt(raw, 10) || 1, 1), Math.max(stock, 1));

  const grossTotal = product.grossPrice * qty;
  const netTotal = product.netPrice * qty;
  const vatTotal = Math.round((withVat(netTotal) - netTotal) * 100) / 100;
  const grandTotal = withVat(netTotal);
  const eur = (v) => (eurRate > 0 ? formatNumber(v / eurRate, 2) : '-');

  const ROWS = [
    ['Birim Brüt Fiyat', product.grossPrice],
    ['Toplam Brüt Fiyat', grossTotal],
  ];

  return (
    <div
      className={styles.overlay}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true" aria-label={`${product.description} detayı`}
    >
      <div className={styles.modal} ref={modalRef}>
        <div className={styles.head}>
          <div>
            <span className={styles.headCode}>{product.productCode}</span>
            <h2 className={styles.headName}>{product.description}</h2>
            <span className={styles.tagRow}>
              <span className={styles.tag}>{product.brand}_{product.groupCode}</span>
              <span className={styles.tag}><Truck size={12} /> {DELIVERY_TIME}</span>
              {(product.discounts || []).some((d) => d.kind === 'general') && (
                <span className={`${styles.tag} ${styles.tagCamp}`}>KAMPANYALI</span>
              )}
            </span>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose}
                  ref={closeRef} aria-label="Kapat">
            <X size={17} />
          </button>
        </div>

        <div className={styles.banner}>
          <span className={styles.bannerIcon}><Warehouse size={16} /></span>
          <span className={styles.bannerText}>
            <b>{wh.label}</b> deposundan sipariş veriyorsunuz
          </span>
          <span className={`${styles.bannerPill} ${out ? styles.bannerOut : ''}`}>
            {out ? <XCircle size={13} /> : <CheckCircle2 size={13} />}
            {out ? 'Stokta yok' : 'Stokta mevcut'}
          </span>
        </div>

        <div className={styles.body}>
          <div className={styles.left}>
            <Galeri product={product} />

            <div className={styles.section}>
              <p className={styles.sectionTitle}>OEM Kodları</p>
              {product.oem && product.oem.length ? (
                <div className={styles.oemWrap}>
                  {product.oem.map((o) => <span key={o} className={styles.oemChip}>{o}</span>)}
                </div>
              ) : <span className={styles.muted}>Kayıt yok</span>}
            </div>

            <div className={styles.section}>
              <p className={styles.sectionTitle}>Araç Uyumu</p>
              <FitmentInfo code={product.productCode} />
            </div>

            <div className={styles.section}>
              <p className={styles.sectionTitle}>Depo Stokları</p>
              {WAREHOUSES.map((w) => {
                const wOut = product[w.key] <= 0;
                return (
                  <div key={w.key}
                       className={`${styles.whRow} ${w.key === whKey ? styles.whRowOn : ''}`}>
                    <span className={styles.whName}>{w.label}</span>
                    <span className={`${styles.whPill} ${wOut ? styles.whPillOut : ''}`}>
                      {wOut ? <XCircle size={12} /> : <CheckCircle2 size={12} />}
                      {wOut ? 'Stok Yok' : 'Mevcut'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.right}>
            {eurRate > 0 && (
              <div className={styles.rateRow}>
                <span>EUR/TRY Kuru</span>
                <span className={styles.rateVal}>{formatNumber(eurRate, 4)}</span>
              </div>
            )}

            <table className={styles.table}>
              <thead>
                <tr><th> </th><th>₺</th><th>€</th></tr>
              </thead>
              <tbody>
                {ROWS.map(([label, val]) => (
                  <tr key={label}>
                    <td>{label}</td>
                    <td>{formatNumber(val, 2)}</td>
                    <td>{eur(val)}</td>
                  </tr>
                ))}
                <tr>
                  <td>İndirim Oranları</td>
                  <td colSpan={2}>
                    <span className={styles.chipCell}>
                      {product.discounts.map((d, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className={styles.arrow}>›</span>}
                          <span className={`${styles.chip} ${d.kind === 'general' ? styles.chipCamp : ''}`}>
                            %{formatNumber(d.rate, 2)}
                          </span>
                        </React.Fragment>
                      ))}
                    </span>
                  </td>
                </tr>
                <tr className={styles.rowHi}>
                  <td>İndirimli Br. Net Fiyat</td>
                  <td>{formatNumber(product.netPrice, 2)}</td>
                  <td>{eur(product.netPrice)}</td>
                </tr>
                <tr>
                  <td>İndirimli Toplam Net</td>
                  <td>{formatNumber(netTotal, 2)}</td>
                  <td>{eur(netTotal)}</td>
                </tr>
                <tr>
                  <td>KDV Tutarı (%{VAT_RATE})</td>
                  <td>{formatNumber(vatTotal, 2)}</td>
                  <td>{eur(vatTotal)}</td>
                </tr>
                <tr>
                  <td>Kazancınız</td>
                  <td className={styles.saveVal}>{formatNumber(product.savingAmount * qty, 2)}</td>
                  <td className={styles.saveVal}>{eur(product.savingAmount * qty)}</td>
                </tr>
                <tr className={styles.rowTotal}>
                  <td>Toplam Tutar (KDV Dahil)</td>
                  <td>{formatNumber(shownTotal, 2)}</td>
                  <td>{eur(shownTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.foot}>
          <div>
            <p className={styles.footLabel}>Sipariş Deposu Seçin</p>
            <div className={styles.whPick}>
              {WAREHOUSES.map((w) => {
                const wOut = product[w.key] <= 0;
                const on = w.key === whKey;
                return (
                  <button
                    key={w.key} type="button" disabled={wOut}
                    className={`${styles.whOpt} ${on ? styles.whOptOn : ''}`}
                    onClick={() => { setWhKey(w.key); setRaw('1'); }}
                  >
                    <span className={`${styles.radio} ${on ? styles.radioOn : ''}`}>
                      {on && <span className={styles.radioDot} />}
                    </span>
                    <span>
                      <span className={styles.optName}>{w.city}</span>
                      <span className={wOut ? styles.optStateOut : styles.optState}>
                        {wOut ? 'Stok Yok' : 'Mevcut'}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className={styles.footLabel}>Miktar</p>
            <span className={styles.qtyBox}>
              <button type="button" className={styles.qtyBtn} disabled={qty <= 1 || out}
                      {...holdDec} aria-label="Azalt">
                <Minus size={14} />
              </button>
              <input
                className={styles.qtyInput} type="text" inputMode="numeric"
                value={out ? '0' : raw} disabled={out}
                onChange={(e) => { const v = e.target.value; if (v === '' || /^[0-9]{0,5}$/.test(v)) setRaw(v); }}
                onBlur={() => setRaw(String(qty))} onFocus={(e) => e.target.select()}
                aria-label="Miktar"
              />
              <button type="button" className={styles.qtyBtn} disabled={qty >= stock || out}
                      {...holdInc} aria-label="Artır">
                <Plus size={14} />
              </button>
            </span>
          </div>

          <div className={styles.footBtns}>
            <button type="button" className={styles.btnGhost} disabled={out}
                    onClick={() => { onAddToCart(product, qty, wh); onClose(); }}>
              <Zap size={15} /> Hızlı Sipariş
            </button>
            <button type="button" className={styles.btnMain} disabled={out}
                    onClick={() => { onAddToCart(product, qty, wh); onClose(); }}>
              <ShoppingCart size={16} />
              {out ? 'Stokta Yok' : `${wh.from} Sepete Ekle`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
